/* eslint-disable @next/next/no-img-element */
import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import GoogleMapReact, { Coords } from "google-map-react";
import cls from "./map.module.scss";
import { MAP_API_KEY } from "constants/constants";
import { getAddressFromLocation } from "utils/getAddressFromLocation";
import { IShop } from "interfaces";
import handleGooglePlacesPress from "utils/handleGooglePlacesPress";
import { useSettings } from "contexts/settings/settings.context";
import Image from "next/image";
import { AddressResolution } from "utils/addressParts";
import { geolocateAddressDetails } from "utils/geolocateAddress";

const Marker = (props: any) => (
  <div className={cls.point}>
    <img
      src={
        props.isOrder ? "/images/client-pin-black.png" : "/images/marker.png"
      }
      width={32}
      alt="location"
    />
  </div>
);
const ShopMarker = (props: any) => (
  <div className={cls.floatCard}>
    <div className={cls.marker}>
      <Image src="/images/pin-shop.svg" alt="Location" width={24} height={28} />
    </div>
  </div>
);

const options = {
  fields: ["address_components", "geometry"],
  types: ["address"],
};

type Props = {
  location: Coords;
  setLocation?: (data: any) => void;
  readOnly?: boolean;
  shop?: IShop;
  inputRef?: MutableRefObject<HTMLInputElement | null>;
  setAddress?: (data: any) => void;
  price?: number;
  drawLine?: boolean;
  defaultZoom?: number;
  isOrder?: boolean;
  onAddressResolved?: (result: AddressResolution) => boolean | void;
  resolutionRevision?: number;
};

export default function Map({
  location,
  setLocation = () => {},
  readOnly = false,
  isOrder = false,
  shop,
  inputRef,
  setAddress,
  price,
  drawLine,
  defaultZoom = 15,
  onAddressResolved,
  resolutionRevision = 0,
}: Props) {
  const { settings } = useSettings();
  const autoCompleteRef = useRef<any>();
  const [maps, setMaps] = useState<any>();
  const [map, setMap] = useState<any>();
  const resolvedEpoch = useRef(0);
  const resolvedMounted = useRef(true);
  const resolvedListener = useRef<any>();
  const resolvedCallbacks = useRef({ setLocation, setAddress, onAddressResolved });
  resolvedCallbacks.current = { setLocation, setAddress, onAddressResolved };
  const structured = !!onAddressResolved;

  useEffect(() => {
    if (!structured) return;
    resolvedMounted.current = true;
    const input = inputRef?.current;
    const invalidate = () => { resolvedEpoch.current += 1; };
    input?.addEventListener("input", invalidate);
    return () => {
      resolvedMounted.current = false;
      invalidate();
      input?.removeEventListener("input", invalidate);
      resolvedListener.current?.remove?.();
    };
  }, [structured, inputRef]);

  // External geolocation cancels a pending map reverse lookup, not its own rerender.
  useEffect(() => { if (structured) resolvedEpoch.current += 1; }, [structured, resolutionRevision]);

  function validPoint(lat: unknown, lng: unknown): Coords | null {
    return typeof lat === "number" && typeof lng === "number" && Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
  }

  function applyResolution(result: AddressResolution, epoch: number) {
    if (!resolvedMounted.current || epoch !== resolvedEpoch.current) return;
    if (resolvedCallbacks.current.onAddressResolved?.(result) === false) return;
    if (inputRef?.current) inputRef.current.value = result.text;
  }

  async function resolveMapPoint(value: any) {
    const epoch = ++resolvedEpoch.current;
    let point: Coords | null = null;
    try { point = validPoint(value?.center?.lat?.(), value?.center?.lng?.()); } catch { /* Invalid provider geometry. */ }
    if (!point) { resolvedCallbacks.current.setLocation(null); resolvedCallbacks.current.setAddress?.("not found"); return; }
    resolvedCallbacks.current.setLocation(point);
    const result = await geolocateAddressDetails(point.lat, point.lng, settings?.google_map_key || MAP_API_KEY || "");
    if (!resolvedMounted.current || epoch !== resolvedEpoch.current) return;
    if (result) applyResolution(result, epoch);
    else resolvedCallbacks.current.setAddress?.("not found");
  }

  async function onChangeMap(map: any) {
    if (readOnly) {
      return;
    }
    if (structured) return resolveMapPoint(map);
    const location = {
      lat: map.center.lat(),
      lng: map.center.lng(),
    };
    setLocation(location);
    const address = await getAddressFromLocation(
      `${location.lat},${location.lng}`,
    );
    if (inputRef?.current) {
      inputRef.current.value = address;
    }
    if (setAddress) setAddress(address);
  }

  const handleApiLoaded = (map: any, maps: any) => {
    autoComplete(map, maps);
    setMap(map);
    setMaps(maps);
    if (shop) {
      const shopLocation = {
        lat: Number(shop.location?.latitude) || 0,
        lng: Number(shop.location?.longitude) || 0,
      };
      const markers = [location, shopLocation];
      let bounds = new maps.LatLngBounds();
      for (let i = 0; i < markers.length; i++) {
        bounds.extend(markers[i]);
      }
      map.fitBounds(bounds);
    }
  };

  function autoComplete(_: any, maps: any) {
    if (inputRef) {
      autoCompleteRef.current = new maps.places.Autocomplete(
        inputRef.current,
        structured ? { ...options, fields: [...options.fields, "formatted_address", "place_id"] } : options,
      );
      const listener = autoCompleteRef.current.addListener("place_changed", async function () {
        if (structured) {
          if (!resolvedMounted.current) return;
          const epoch = ++resolvedEpoch.current;
          const place = autoCompleteRef.current.getPlace();
          let point: Coords | null = null;
          try { point = validPoint(place?.geometry?.location?.lat?.(), place?.geometry?.location?.lng?.()); } catch { /* Invalid provider geometry. */ }
          const address = typeof place?.formatted_address === "string" ? place.formatted_address.trim() : inputRef.current?.value.trim();
          if (!point || !address || address.toLowerCase() === "not found") {
            resolvedCallbacks.current.setLocation(null);
            resolvedCallbacks.current.setAddress?.("not found"); return;
          }
          resolvedCallbacks.current.setLocation(point);
          applyResolution({ text: address, components: place?.address_components, location: point, placeId: typeof place?.place_id === "string" ? place.place_id : undefined }, epoch);
          return;
        }
        const place = await autoCompleteRef.current.getPlace();
        const address = handleGooglePlacesPress(place);
        const coords: Coords = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setLocation(coords);
        if (setAddress) setAddress(address);
      });
      if (structured) resolvedListener.current = listener;
    }
  }

  useEffect(() => {
    if (shop && maps) {
      const shopLocation = {
        lat: Number(shop.location?.latitude) || 0,
        lng: Number(shop.location?.longitude) || 0,
      };
      const markers = [location, shopLocation];
      let bounds = new maps.LatLngBounds();
      for (let i = 0; i < markers.length; i++) {
        bounds.extend(markers[i]);
      }
      map.fitBounds(bounds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, shop?.location, drawLine, map, maps]);

  return (
    <div className={cls.root}>
      {!readOnly && (
        <div className={cls.marker}>
          <img src="/images/marker.png" width={32} alt="Location" />
        </div>
      )}
      <GoogleMapReact
        bootstrapURLKeys={{
          key: settings?.google_map_key || MAP_API_KEY || "",
          libraries: ["places"],
        }}
        zoom={defaultZoom}
        center={location}
        onDragEnd={onChangeMap}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
        options={{ fullscreenControl: readOnly }}
      >
        {readOnly && (
          <Marker lat={location?.lat} lng={location?.lng} isOrder={isOrder} />
        )}
        {!!shop && (
          <ShopMarker
            lat={shop.location?.latitude || 0}
            lng={shop.location?.longitude || 0}
            shop={shop}
            price={price}
          />
        )}
      </GoogleMapReact>
    </div>
  );
}
